import { Box, Button, Heading, Table, VisuallyHidden } from '@chakra-ui/react'
import { FiDownload } from 'react-icons/fi'
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  Tooltip, XAxis, YAxis,
} from 'recharts'
import type { ChartBlock } from '../../../types/reporting'
import { ChartFrame, chartTheme, seriesColor } from '../../shared/viz'

/**
 * Charts render at a FIXED width rather than through ResponsiveContainer.
 *
 * ResponsiveContainer measures its parent during layout, and in print layout some browsers report
 * that parent as 0px wide — producing a blank chart in the PDF while looking perfect on screen.
 * Since PDF export is the browser's own print (spec D4), a chart that cannot print is a chart that
 * cannot ship. A fixed width prints correctly and still scales down on mobile via maxW.
 *
 * The width is set as an inline `style` (not a Chakra style prop) so it cannot be lost to CSS
 * extraction, media-query specificity, or a print stylesheet reset -- and so the guarding test
 * can assert it directly from the DOM rather than through jsdom's limited computed-style support.
 */
export const PRINT_CHART_WIDTH = 640
const CHART_HEIGHT = 280

interface Props {
  block: ChartBlock
  onExportCsv?: (blockId: string) => void
}

export function ChartBlockView({ block, onExportCsv }: Props) {
  const common = {
    width: PRINT_CHART_WIDTH,
    height: CHART_HEIGHT,
    data: block.rows as Record<string, number | string>[],
  }

  return (
    <Box className="report-block">
      {!!block.title && <Heading size="sm" mb={2}>{block.title}</Heading>}
      {!!onExportCsv && (
        <Button
          className="no-print"
          size="xs" variant="outline" mb={2}
          onClick={() => onExportCsv(block.id)}
        >
          <FiDownload /> CSV
        </Button>
      )}
      <ChartFrame ariaLabel={block.title ?? 'chart'} printWidth={PRINT_CHART_WIDTH} height={CHART_HEIGHT}>
        {block.variant === 'Line' ? (
          <LineChart {...common}>
            <CartesianGrid {...chartTheme.grid} />
            <XAxis dataKey={block.xKey} label={{ value: block.xLabel, position: 'insideBottom', offset: -4 }}
                   tick={chartTheme.axis.tick} stroke={chartTheme.axis.stroke} />
            <YAxis tick={chartTheme.axis.tick} stroke={chartTheme.axis.stroke} />
            <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
            <Legend />
            {block.series.map((s, i) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                    stroke={seriesColor(i)} dot={false} />
            ))}
          </LineChart>
        ) : block.variant === 'Donut' ? (
          <PieChart width={PRINT_CHART_WIDTH} height={CHART_HEIGHT}>
            <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
            <Legend />
            <Pie data={common.data} dataKey={block.series[0]?.key ?? 'value'}
                 nameKey={block.xKey} innerRadius={60} outerRadius={100}>
              {common.data.map((_, i) => (
                <Cell key={i} fill={seriesColor(i)} />
              ))}
            </Pie>
          </PieChart>
        ) : (
          <BarChart {...common}>
            <CartesianGrid {...chartTheme.grid} />
            <XAxis dataKey={block.xKey} label={{ value: block.xLabel, position: 'insideBottom', offset: -4 }}
                   tick={chartTheme.axis.tick} stroke={chartTheme.axis.stroke} />
            <YAxis tick={chartTheme.axis.tick} stroke={chartTheme.axis.stroke} />
            <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
            <Legend />
            {block.series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.label}
                   stackId={block.variant === 'Stacked' ? 'a' : undefined}
                   fill={seriesColor(i)} />
            ))}
          </BarChart>
        )}
      </ChartFrame>

      {/* A chart is an image to a screen reader. The same rows as a table keeps the report
          readable without sight, and keeps screen content identical to the CSV. */}
      <VisuallyHidden>
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>{block.xLabel}</Table.ColumnHeader>
              {block.series.map((s) => <Table.ColumnHeader key={s.key}>{s.label}</Table.ColumnHeader>)}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {block.rows.map((row, i) => (
              <Table.Row key={i}>
                <Table.Cell>{String(row[block.xKey] ?? '')}</Table.Cell>
                {block.series.map((s) => <Table.Cell key={s.key}>{String(row[s.key] ?? '')}</Table.Cell>)}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </VisuallyHidden>
    </Box>
  )
}
